import { useMemo } from 'react'
import { Euler, Plane, Quaternion, Vector2, Vector3 } from 'three'
import {
  ImmutableVector3,
  getCenterMultipleReduceMean,
  getFlatShapeNormal,
  toImmutable,
} from '@modugen/ui-toolkit'
import getPlaneLineIntersection from 'src/components/pages/Editor/utils/getPlaneLineIntersection'

const defaultOffsetZ = 0.3

const useSlabIconFromCenter = (
  slab: ShapeObject,
  options: {
    offsetZ?: number
    /**
     * Offset x is from the center of the slab in x direction. This is needed if
     * you want to show multiple icons on the same slab.
     */
    offsetX?: number
    iconSizeX: number
    iconSizeY: number
  },
) => {
  const offset = options?.offsetZ ?? defaultOffsetZ

  return useMemo(() => {
    const p0 = slab.shape.points[0]
    const p1 = slab.shape.points[1]

    const centerOfSlab = getCenterMultipleReduceMean(slab.shape.points)

    const edge0 = p1.sub(p0)

    const xDirection = new ImmutableVector3(1, 0, 0)
    const yDirection = new ImmutableVector3(0, 1, 0)

    const centerOffsettedBySnowIconSize = centerOfSlab
      .addScaledVector(xDirection, -options.iconSizeX)
      .addScaledVector(yDirection, options.iconSizeY)

    const plane = new Plane().setFromCoplanarPoints(
      slab.shape.points[0].v,
      slab.shape.points[1].v,
      slab.shape.points[2].v,
    )

    const pointOnPlane = getPlaneLineIntersection(
      plane,
      new Vector2(centerOffsettedBySnowIconSize.x, centerOffsettedBySnowIconSize.y),
    )!

    // Point needs to be offsetted so it is not within the center and on top of
    // arrow orientation
    const pointOffsetted = pointOnPlane.addScaledVector(edge0.normalize(), options.offsetX ?? 0)

    const pointForIcon = toImmutable(pointOffsetted.v.setZ(pointOffsetted.z + offset))

    const slabNormal = getFlatShapeNormal(
      slab.shape.points as [
        ImmutableVector3,
        ImmutableVector3,
        ImmutableVector3,
        ...ImmutableVector3[],
      ],
    )

    const defaultDir = new Vector3(0, 0, 1)
    const quaternion = new Quaternion().setFromUnitVectors(defaultDir, slabNormal)
    const euler = new Euler().setFromQuaternion(quaternion)

    return {
      pointForIcon: pointForIcon,
      rotation: euler,
    }
  }, [offset, options.iconSizeX, options.iconSizeY, options.offsetX, slab.shape.points])
}

export default useSlabIconFromCenter
